import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { Suspense, lazy } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import MobileQuickAction from "./components/MobileQuickAction";
import WhatsAppButton from "./components/WhatsAppButton";
import { Toaster } from "./components/ui/toaster";

// Statically imported pages — these are the pages we pre-render (see /app/prerender.py).
// Pre-rendered pages MUST NOT sit inside a Suspense boundary, otherwise React 19's hydration
// looks for `<!--$-->` markers in the prerendered HTML that Playwright can't emit.
import Home from "./pages/Home";
import PrayerRequest from "./pages/PrayerRequest";
import SacredSites from "./pages/SacredSites";
import Testimonials from "./pages/Testimonials";
import HolyWord from "./pages/HolyWord";
import Pricing from "./pages/Pricing";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Refund from "./pages/Refund";
import LandingEmotional from "./pages/LandingEmotional";
import LandingProof from "./pages/LandingProof";
import LandingChurches from "./pages/LandingChurches";
import LandingLent from "./pages/LandingLent";

// Lazy-loaded pages — not pre-rendered, code-split for a smaller initial bundle.
const PrayerWall = lazy(() => import("./pages/PrayerWall"));
const PrayerLibrary = lazy(() => import("./pages/PrayerLibrary"));
const Article = lazy(() => import("./pages/Article"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyPrayer = lazy(() => import("./pages/VerifyPrayer"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const LeaveReview = lazy(() => import("./pages/LeaveReview"));
const UrgentRush = lazy(() => import("./pages/UrgentRush"));
const PrayAtHolySepulchre = lazy(() => import("./pages/PrayAtHolySepulchre"));
const HolySitePage = lazy(() => import("./pages/HolySitePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
import { holySitePages } from "./data/holySitePages";
import { prayerTopics } from "./data/prayerTopics";
const PrayerTopicPage = lazy(() => import("./pages/PrayerTopicPage"));

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-stone-50">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

// Wraps a lazy element in its own Suspense so static routes stay hydration-clean.
const L = (element) => <Suspense fallback={<PageLoader />}>{element}</Suspense>;

// Landing page paths where header/footer should be hidden
const LANDING_PAGES = ['/welcome', '/get-started', '/churches', '/lent'];

// Layout wrapper to conditionally show/hide header/footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isLandingPage = LANDING_PAGES.includes(location.pathname);
  
  return (
    <>
      <ScrollToTop />
      {!isLandingPage && <Header />}
      {children}
      {!isLandingPage && <Footer />}
      {!isLandingPage && <MobileQuickAction />}
      {!isLandingPage && <WhatsAppButton />}
      <Toaster />
    </>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <Layout>
            <Routes>
              {/* Statically imported, pre-rendered — NO Suspense wrapper. */}
              <Route path="/" element={<Home />} />
              <Route path="/prayer-request" element={<PrayerRequest />} />
              <Route path="/sacred-sites" element={<SacredSites />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/holy-word" element={<HolyWord />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/welcome" element={<LandingEmotional />} />
              <Route path="/get-started" element={<LandingProof />} />
              <Route path="/churches" element={<LandingChurches />} />
              <Route path="/lent" element={<LandingLent />} />
              {/* Lazy — each gets its own Suspense boundary. */}
              <Route path="/prayer-wall" element={L(<PrayerWall />)} />
              <Route path="/prayer-library" element={L(<PrayerLibrary />)} />
              <Route path="/article/:id" element={L(<Article />)} />
              <Route path="/checkout" element={L(<Checkout />)} />
              <Route path="/checkout/success" element={L(<CheckoutSuccess />)} />
              <Route path="/login" element={L(<Login />)} />
              <Route path="/signup" element={L(<Signup />)} />
              <Route path="/dashboard" element={L(<Dashboard />)} />
              <Route path="/admin" element={L(<AdminDashboard />)} />
              <Route path="/forgot-password" element={L(<ForgotPassword />)} />
              <Route path="/reset-password" element={L(<ResetPassword />)} />
              <Route path="/verify" element={L(<VerifyPrayer />)} />
              <Route path="/unsubscribe" element={L(<Unsubscribe />)} />
              <Route path="/blog" element={L(<Blog />)} />
              <Route path="/blog/:articleId" element={L(<BlogArticle />)} />
              <Route path="/leave-a-review" element={L(<LeaveReview />)} />
              <Route path="/urgent" element={L(<UrgentRush />)} />
              <Route path="/pray-at-the-church-of-the-holy-sepulchre" element={L(<PrayAtHolySepulchre />)} />
              {prayerTopics.map((t) => (
                <Route key={t.slug} path={`/${t.slug}`} element={L(<PrayerTopicPage slug={t.slug} />)} />
              ))}
              {holySitePages.map((p) => (
                <Route key={p.slug} path={`/${p.slug}`} element={L(<HolySitePage slug={p.slug} />)} />
              ))}
              <Route path="*" element={L(<NotFound />)} />
            </Routes>
          </Layout>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
